import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase } from '../lib/typeHelper'

export interface InsuranceCarrierContact {
  id: string
  name: string
  email: string
  phone: string
  title: string
  isPrimary: boolean
}

/** AM Best financial-strength rating, best to worst. Mirrors InsuranceCarrier.Rating. */
export enum CarrierRatingValue {
  A_PLUS_PLUS = 'A++',
  A_PLUS = 'A+',
  A = 'A',
  A_MINUS = 'A-',
  B_PLUS_PLUS = 'B++',
  B_PLUS = 'B+',
  B = 'B',
  B_MINUS = 'B-',
  C_PLUS_PLUS = 'C++',
  C_PLUS = 'C+',
  C = 'C',
  C_MINUS = 'C-',
  D = 'D',
  /** What a broker or RRG publishes when the paper behind a risk varies. */
  A_MINUS_OR_BETTER = 'A- or better',
}

/** Whether the state guaranty fund backstops a policy on this carrier's paper. */
export enum CarrierAdmittedStatus {
  ADMITTED = 'admitted',
  NON_ADMITTED = 'non_admitted',
}

export const CARRIER_ADMITTED_STATUS_LABELS: Record<CarrierAdmittedStatus, string> = {
  [CarrierAdmittedStatus.ADMITTED]: 'Admitted',
  [CarrierAdmittedStatus.NON_ADMITTED]: 'Non-admitted',
}

/** A name a vendor or document uses for this carrier, and the paper it means. */
export interface InsuranceCarrierAlias {
  id: string
  name: string
  admittedStatus: CarrierAdmittedStatus | ''
  notes: string
}

/** How quickly a carrier issues a COI. Mirrors InsuranceCarrier.CoiSpeed. */
export enum CarrierCoiSpeed {
  UNDER_24_HOURS = 'under_24_hours',
  ONE_DAY_OR_MORE = 'one_day_or_more',
}

export const CARRIER_COI_SPEED_LABELS: Record<CarrierCoiSpeed, string> = {
  [CarrierCoiSpeed.UNDER_24_HOURS]: '24 hours or less',
  [CarrierCoiSpeed.ONE_DAY_OR_MORE]: 'More than 24 hours',
}

/** The kind of insurance a carrier writes. Mirrors InsuranceCarrier.Industry. */
export enum CarrierIndustry {
  TRUCKING = 'TRUCKING',
  PET = 'PET',
  WORKERS_COMP = 'WORKERS_COMP',
}

export const CARRIER_INDUSTRY_LABELS: Record<CarrierIndustry, string> = {
  [CarrierIndustry.TRUCKING]: 'Trucking',
  [CarrierIndustry.PET]: 'Pet',
  [CarrierIndustry.WORKERS_COMP]: 'Workers comp',
}

/** What a pet carrier doesn't have: these are about commercial trucking paper. */
export const PET_HIDDEN_PROFILE_FIELDS = ['admittedStatus', 'rating', 'coiSpeed'] as const

/**
 * The curated bits a customer compares carriers on. Each claims capability pairs a
 * boolean (what the quotes-page filter checks) with the prose shown on the card.
 */
export interface CarrierProfile {
  /** Shown to customers on the quote card, under "Notes from <broker>". */
  publicNote: string
  rating: CarrierRatingValue | ''
  coiSpeed: CarrierCoiSpeed | ''
  /** Shown on the quote card as "Insurance type"; E&S paper is inherently non-admitted. */
  admittedStatus: CarrierAdmittedStatus | ''
  /** Free text describing the financing on offer; empty means none available. */
  financingOptions: string
  /** The carrier prices each coverage separately, so the card itemises "Add to cart". */
  allowsIndividualCoveragePurchase: boolean
  claimsHasMultipleWaysToFile: boolean
  claimsWaysToFile: string
  claimsHas247Support: boolean
  claimsSupportAvailability: string
  claimsHasPayoutInformation: boolean
  claimsPayoutSpeed: string
  claimsWaysToGetPaid: string
}

export interface InsuranceCarrier extends CarrierProfile {
  id: string
  name: string
  /** Customer-facing name shown on Quote Cards, Indication Cards, and Cai estimates. */
  displayName: string
  naicCode: string
  website: string
  primaryEmail: string
  primaryPhone: string
  notes: string
  logo: string | null
  /** Greyscale logo used on the public marketing site. */
  marketingLogo: string | null
  isWholesaler: boolean
  industry: CarrierIndustry
  contacts: InsuranceCarrierContact[]
  aliases: InsuranceCarrierAlias[]
  createdAt: string
  updatedAt: string
}

export interface CarrierInput extends Partial<CarrierProfile> {
  name: string
  displayName?: string
  naicCode?: string
  website?: string
  primaryEmail?: string
  primaryPhone?: string
  notes?: string
  logo?: File | null
  marketingLogo?: File | null
  isWholesaler?: boolean
  industry?: CarrierIndustry
}

export interface ContactInput {
  name: string
  email?: string
  phone?: string
  title?: string
  isPrimary?: boolean
}

export interface AliasInput {
  name: string
  admittedStatus?: CarrierAdmittedStatus | ''
  notes?: string
}

const BASE = '/api/v1/insure/internal/insurance-carriers/'

// Send multipart when a logo File is present (the API accepts JSON or multipart),
// otherwise a plain JSON body. Field names are snake_case for the DRF serializer.
function buildCarrierBody(input: CarrierInput): FormData | Record<string, string> {
  const fields: Record<string, string> = {}
  fields.name = input.name
  if (input.displayName !== undefined) fields.display_name = input.displayName
  if (input.naicCode !== undefined) fields.naic_code = input.naicCode
  if (input.website !== undefined) fields.website = input.website
  if (input.primaryEmail !== undefined) fields.primary_email = input.primaryEmail
  if (input.primaryPhone !== undefined) fields.primary_phone = input.primaryPhone
  if (input.notes !== undefined) fields.notes = input.notes
  if (input.publicNote !== undefined) fields.public_note = input.publicNote
  if (input.rating !== undefined) fields.rating = input.rating
  if (input.coiSpeed !== undefined) fields.coi_speed = input.coiSpeed
  if (input.admittedStatus !== undefined) fields.admitted_status = input.admittedStatus
  if (input.financingOptions !== undefined) fields.financing_options = input.financingOptions
  // Booleans go over multipart as strings, which DRF's BooleanField parses.
  if (input.allowsIndividualCoveragePurchase !== undefined)
    fields.allows_individual_coverage_purchase = String(input.allowsIndividualCoveragePurchase)
  if (input.claimsHasMultipleWaysToFile !== undefined)
    fields.claims_has_multiple_ways_to_file = String(input.claimsHasMultipleWaysToFile)
  if (input.claimsWaysToFile !== undefined) fields.claims_ways_to_file = input.claimsWaysToFile
  if (input.claimsHas247Support !== undefined)
    fields.claims_has_24_7_support = String(input.claimsHas247Support)
  if (input.claimsSupportAvailability !== undefined)
    fields.claims_support_availability = input.claimsSupportAvailability
  if (input.claimsHasPayoutInformation !== undefined)
    fields.claims_has_payout_information = String(input.claimsHasPayoutInformation)
  if (input.claimsPayoutSpeed !== undefined) fields.claims_payout_speed = input.claimsPayoutSpeed
  if (input.claimsWaysToGetPaid !== undefined) fields.claims_ways_to_get_paid = input.claimsWaysToGetPaid
  if (input.isWholesaler !== undefined) fields.is_wholesaler = String(input.isWholesaler)
  if (input.industry !== undefined) fields.industry = input.industry

  if (input.logo instanceof File || input.marketingLogo instanceof File) {
    const formData = new FormData()
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value))
    if (input.logo instanceof File) formData.append('logo', input.logo)
    if (input.marketingLogo instanceof File) formData.append('marketing_logo', input.marketingLogo)
    return formData
  }
  return fields
}

function getCarrierPage(page: number): Promise<PaginatedResults<InsuranceCarrier>> {
  return axios
    .get(BASE, { params: { page } })
    .then((response) => toCamelCase(response.data) as PaginatedResults<InsuranceCarrier>)
}

// The page number out of DRF's `next` link, or null once there is no next page.
function nextPageParam(url: string | null): number | null {
  if (!url) return null
  const page = new URL(url, window.location.origin).searchParams.get('page')
  return page ? Number(page) : null
}

// Every page: DRF paginates at 100, and the merge dialog has to reach a duplicate on any of them.
async function getCarriers(): Promise<PaginatedResults<InsuranceCarrier>> {
  const results: InsuranceCarrier[] = []
  let count = 0
  let page: number | null = 1
  while (page !== null) {
    const response: PaginatedResults<InsuranceCarrier> = await getCarrierPage(page)
    results.push(...response.results)
    count = response.count
    page = nextPageParam(response.next)
  }
  return { count, next: null, previous: null, results }
}

export function useInsuranceCarriers() {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.CARRIERS],
    queryFn: getCarriers,
  })
  return { carriers: data?.results ?? [], count: data?.count ?? 0, isLoading }
}

export function useCreateCarrier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CarrierInput) =>
      axios
        .post(BASE, buildCarrierBody(input))
        .then((response) => toCamelCase(response.data) as InsuranceCarrier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CARRIERS] })
      // Lead details embed carrier records (assigned_carriers), so refresh them too.
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useUpdateCarrier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: CarrierInput & { id: string }) =>
      axios
        .patch(`${BASE}${id}/`, buildCarrierBody(input))
        .then((response) => toCamelCase(response.data) as InsuranceCarrier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CARRIERS] })
      // Lead details embed carrier records (assigned_carriers), so refresh them too.
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useDeleteCarrier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => axios.delete(`${BASE}${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CARRIERS] })
      // Lead details embed carrier records (assigned_carriers), so refresh them too.
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

// Moves every row off the source carrier, keeps its name as an alias, deletes it.
export function useMergeCarrier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      targetId,
      sourceId,
      admittedStatus,
    }: {
      targetId: string
      sourceId: string
      admittedStatus?: CarrierAdmittedStatus | ''
    }) =>
      axios
        .post(`${BASE}${targetId}/merge/`, {
          source_id: sourceId,
          admitted_status: admittedStatus ?? '',
        })
        .then((response) => toCamelCase(response.data) as InsuranceCarrier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CARRIERS] })
      // Lead details embed carrier records (assigned_carriers), so refresh them too.
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

function contactBody(input: ContactInput): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  body.name = input.name
  if (input.email !== undefined) body.email = input.email
  if (input.phone !== undefined) body.phone = input.phone
  if (input.title !== undefined) body.title = input.title
  if (input.isPrimary !== undefined) body.is_primary = input.isPrimary
  return body
}

export function useUpsertCarrierContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      carrierId,
      contactId,
      ...input
    }: ContactInput & { carrierId: string; contactId?: string }) => {
      const body = contactBody(input)
      const request = contactId
        ? axios.patch(`${BASE}${carrierId}/contacts/${contactId}/`, body)
        : axios.post(`${BASE}${carrierId}/contacts/`, body)
      return request.then((response) => toCamelCase(response.data) as InsuranceCarrier)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CARRIERS] })
      // Lead details embed carrier records (assigned_carriers), so refresh them too.
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useDeleteCarrierContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ carrierId, contactId }: { carrierId: string; contactId: string }) =>
      axios.delete(`${BASE}${carrierId}/contacts/${contactId}/delete/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CARRIERS] })
      // Lead details embed carrier records (assigned_carriers), so refresh them too.
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

function aliasBody(input: AliasInput): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  body.name = input.name
  if (input.admittedStatus !== undefined) body.admitted_status = input.admittedStatus
  if (input.notes !== undefined) body.notes = input.notes
  return body
}

export function useUpsertCarrierAlias() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ carrierId, aliasId, ...input }: AliasInput & { carrierId: string; aliasId?: string }) => {
      const body = aliasBody(input)
      const request = aliasId
        ? axios.patch(`${BASE}${carrierId}/aliases/${aliasId}/`, body)
        : axios.post(`${BASE}${carrierId}/aliases/`, body)
      return request.then((response) => toCamelCase(response.data) as InsuranceCarrier)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CARRIERS] })
      // Lead details embed carrier records (assigned_carriers), so refresh them too.
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

export function useDeleteCarrierAlias() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ carrierId, aliasId }: { carrierId: string; aliasId: string }) =>
      axios.delete(`${BASE}${carrierId}/aliases/${aliasId}/delete/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CARRIERS] })
      // Lead details embed carrier records (assigned_carriers), so refresh them too.
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

// Replace the whole set of carriers assigned to a lead (mirrors useSetLeadTags).
export function useSetLeadCarriers() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, carrierIds }: { leadId: string; carrierIds: string[] }) =>
      axios.put(`/api/v1/insure/internal/sales-leads/${leadId}/assigned-carriers/`, {
        carrier_ids: carrierIds,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] }),
  })
}

export function useSendCarrierEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      leadId,
      carrierId,
      contactId,
      subject,
      content,
      htmlContent,
      inReplyToActivityId,
    }: {
      leadId: string
      carrierId: string
      contactId: string
      subject: string
      content: string
      htmlContent?: string
      inReplyToActivityId?: string
    }) =>
      axios.post(`/api/v1/insure/internal/sales-leads/${leadId}/send-carrier-email/`, {
        carrier_id: carrierId,
        contact_id: contactId,
        subject,
        content,
        html_content: htmlContent,
        in_reply_to_activity_id: inReplyToActivityId,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] }),
  })
}

export function useSendCarrierSms() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      leadId,
      carrierId,
      contactId,
      content,
    }: {
      leadId: string
      carrierId: string
      contactId: string
      content: string
    }) =>
      axios.post(`/api/v1/insure/internal/sales-leads/${leadId}/send-carrier-sms/`, {
        carrier_id: carrierId,
        contact_id: contactId,
        content,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] }),
  })
}
